import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Plus, Lock, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

export default function ConversationSidebar({
    open,
    onClose,
    userToken,
    currentSessionId,
    onSelect,
    onNew,
    refreshKey,
}) {
    const [loading, setLoading] = useState(false);
    const [convs, setConvs] = useState([]);

    useEffect(() => {
        if (!open || !userToken) return;
        let cancelled = false;
        setLoading(true);
        axios
            .get(`${API}/conversations`, { params: { user_token: userToken } })
            .then((r) => {
                if (!cancelled) setConvs(r.data || []);
            })
            .catch(() => {
                if (!cancelled) setConvs([]);
            })
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [open, userToken, refreshKey]);

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
                        onClick={onClose}
                        data-testid="sidebar-overlay"
                    />
                    <motion.aside
                        initial={{ x: "-100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={{ type: "tween", duration: 0.25 }}
                        className="fixed top-0 left-0 bottom-0 z-50 w-[88%] sm:w-[380px] bg-rt-surface border-r border-rt-border flex flex-col"
                        data-testid="sidebar"
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-rt-border">
                            <div className="font-serif text-lg tracking-tight">
                                Your conversations
                            </div>
                            <button
                                onClick={onClose}
                                className="text-rt-muted hover:text-rt-text transition"
                                aria-label="Close sidebar"
                                data-testid="sidebar-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <button
                            onClick={() => {
                                onNew?.();
                                onClose?.();
                            }}
                            className="mx-4 mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#B392F0] hover:bg-[#C8B1F5] text-black font-medium text-sm px-4 py-2.5 transition-all hover:-translate-y-[1px]"
                            data-testid="sidebar-new-btn"
                        >
                            <Plus className="w-4 h-4" />
                            New conversation
                        </button>

                        <div className="flex-1 overflow-y-auto p-4 space-y-2">
                            {loading && (
                                <div className="text-center text-rt-muted text-sm py-8">
                                    Loading…
                                </div>
                            )}
                            {!loading && convs.length === 0 && (
                                <div className="text-center text-rt-muted text-sm py-8">
                                    No past conversations yet.
                                </div>
                            )}
                            {!loading &&
                                convs.map((c) => {
                                    const active =
                                        c.session_id === currentSessionId;
                                    return (
                                        <button
                                            key={c.session_id}
                                            onClick={() => {
                                                onSelect?.(c.session_id);
                                                onClose?.();
                                            }}
                                            className={`w-full text-left rounded-2xl border p-3 transition-all ${
                                                active
                                                    ? "border-[#B392F0]/40 bg-[#B392F0]/[0.06]"
                                                    : "border-rt-border bg-rt-card hover:border-rt-border/80 hover:-translate-y-[1px]"
                                            }`}
                                            data-testid={`sidebar-conv-${c.session_id}`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <MessageCircle className="w-4 h-4 mt-0.5 shrink-0 text-rt-dim" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-rt-text leading-snug line-clamp-2">
                                                        {c.title}
                                                    </div>
                                                    <div className="mt-1 flex items-center gap-2 text-[11px] text-rt-dim">
                                                        <span>
                                                            {timeAgo(c.last_activity)}
                                                        </span>
                                                        <span>·</span>
                                                        <span>
                                                            {c.message_count} msg
                                                            {c.message_count === 1
                                                                ? ""
                                                                : "s"}
                                                        </span>
                                                        {c.has_unlocked ? (
                                                            <>
                                                                <span>·</span>
                                                                <span className="inline-flex items-center gap-0.5 text-[#B392F0]">
                                                                    <CheckCircle2 className="w-3 h-3" />
                                                                    unlocked
                                                                </span>
                                                            </>
                                                        ) : (
                                                            c.message_count >= 2 && (
                                                                <>
                                                                    <span>·</span>
                                                                    <Lock className="w-3 h-3" />
                                                                </>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                        </div>

                        <div className="px-5 py-4 border-t border-rt-border text-[11px] text-rt-dim leading-relaxed">
                            Conversations are stored anonymously against this
                            browser only. Clearing your browser data removes
                            them.
                        </div>
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
